import { appConfig } from '../config/env';
import type {
  BridgeMessage,
  BridgePayloadByEvent,
  WidgetChannel,
  WidgetEventType,
} from '../types/bridge';
import { parseWidgetChannel } from './channel';
import { resolveHostOrigin } from './configHelpers';

export { parseWidgetChannel } from './channel';

type NativeHostWindow = Window & {
  webkit?: {
    messageHandlers?: {
      avatarHost?: { postMessage: (message: unknown) => void };
    };
  };
  AvatarNative?: { postMessage: (message: string) => void };
};

/**
 * True inside the iOS SDK. Its WKWebView file menu always offers the camera,
 * so the attach button asks the native side for a picker instead (BR-15).
 * Android handles `<input type=file>` itself and doesn't need this.
 */
export function hasNativeFilePicker(): boolean {
  return Boolean(
    (window as NativeHostWindow).webkit?.messageHandlers?.avatarHost,
  );
}

function postToNativeHost(message: BridgeMessage): void {
  const json = JSON.stringify(message);
  const nativeWindow = window as NativeHostWindow;
  try {
    nativeWindow.webkit?.messageHandlers?.avatarHost?.postMessage(json);
  } catch {
    // Host has no WKScriptMessageHandler — ignore (browser / iframe).
  }
  try {
    nativeWindow.AvatarNative?.postMessage(json);
  } catch {
    // Host has no JavascriptInterface — ignore.
  }
}

export function postToHost<E extends WidgetEventType>(
  event: E,
  payload: Partial<BridgePayloadByEvent[E]> = {},
): void {
  // Callers pass partial extras; timestamp is always stamped here.
  const message: BridgeMessage = {
    source: 'avatar_core_iframe',
    event,
    payload: {
      timestamp: Date.now(),
      ...payload,
    },
  } as BridgeMessage;
  postToNativeHost(message);
  const hostOrigin = resolveHostOrigin();
  if (window.parent !== window && hostOrigin) {
    // Target the embedding page, not the panel origin: the loader runs in the
    // customer's page, so a panel-origin target is silently dropped there.
    // No target when the parent is hidden: '' would throw.
    window.parent.postMessage(message, hostOrigin);
  }
}

export interface PanelQueryParams {
  channel: WidgetChannel;
  publishableKey: string;
  /** Optional session id from loader (`?session=`) — proposal §5.1.5 */
  sessionId: string;
  /** iOS bundle id / Android package from the native SDK (`?bundle=`). */
  bundleId: string;
  /** Host-requested language from loader boot / SDK configure (`?locale=`). */
  requestedLocale: string;
}

export function readQueryParams(): PanelQueryParams {
  const params = new URLSearchParams(window.location.search);
  const channel = parseWidgetChannel(params.get('surface'));
  // Embedded panels always get ?pk=; the fallback is so a bare
  // http://localhost:5173 still loads config in dev (empty elsewhere).
  const publishableKey = params.get('pk') || appConfig.devPublishableKey;
  return {
    channel,
    publishableKey,
    sessionId: params.get('session') || '',
    bundleId: (params.get('bundle') || '').trim(),
    requestedLocale: (params.get('locale') || '').trim(),
  };
}

export function resolvePublishableKey(payload: {
  publishableKey?: string;
  publicKey?: string;
}): string {
  return payload.publishableKey || payload.publicKey || '';
}
